/**
 * HTTP client for the OMBOR API (SPEC.md §8.1, §9.1).
 *
 * Three rules shape this module:
 *
 * 1. **The access token lives in memory only.** Never `localStorage` — anything a script can read,
 *    an XSS payload can steal. It is lost on reload, which is fine: the httpOnly refresh cookie
 *    silently restores the session on boot.
 * 2. **Refresh is single-flight, across tabs.** Rotation revokes the presented token, so two tabs
 *    refreshing with the same cookie would make the loser look like a replay — and the server would
 *    correctly burn the whole family, logging the user out. `navigator.locks` serialises them.
 * 3. **Every failure is the §9.1 envelope**, turned into an `ApiError` carrying a stable code that
 *    the UI translates. Backend sentences are never shown to the user.
 */

/** Base URL of the API, e.g. `http://localhost:8000/api/v1`. */
export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

const CSRF_COOKIE = 'ombor_csrf'
const CSRF_HEADER = 'X-CSRF-Token'
const REFRESH_LOCK = 'ombor.auth.refresh'

/** Thrown for any non-2xx response, carrying the server's error code when one was sent. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: unknown[] = [],
    readonly params: Record<string, unknown> = {},
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** i18n key for this failure, e.g. `errors.INVALID_CREDENTIALS`. */
  get messageKey(): string {
    return `errors.${this.code}`
  }
}

interface ErrorEnvelope {
  error?: {
    code?: string
    message?: string
    details?: unknown[]
    params?: Record<string, unknown>
  }
}

let accessToken: string | null = null
let onSessionExpired: (() => void) | null = null
let onPasswordChangeRequired: (() => void) | null = null

/**
 * The code the API answers on every route but `/auth/me`, `/auth/me/password` and `/auth/logout*`
 * while the signed-in user still holds a temporary password.
 */
export const PASSWORD_CHANGE_REQUIRED = 'PASSWORD_CHANGE_REQUIRED'

/** Store the in-memory access token, or clear it with `null`. */
export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

/**
 * Register what should happen when the session cannot be restored (the auth store resets and the
 * router sends the user to `/login`). A callback avoids importing the store here, which would make
 * the transport layer depend on application state.
 */
export function setSessionExpiredHandler(handler: (() => void) | null): void {
  onSessionExpired = handler
}

/**
 * Register what should happen when the server refuses a request because the user must first
 * replace a temporary password (the router sends them to the change-password screen). A callback
 * for the same reason as `setSessionExpiredHandler`: transport code knows no router.
 */
export function setPasswordChangeRequiredHandler(handler: (() => void) | null): void {
  onPasswordChangeRequired = handler
}

/** Read the readable half of the double-submit CSRF pair. */
export function readCsrfCookie(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]*)`))
  return match?.[1] ? decodeURIComponent(match[1]) : null
}

async function toApiError(response: Response): Promise<ApiError> {
  // A proxy or network appliance may answer with HTML rather than our envelope; falling back to
  // the status text keeps the failure legible instead of throwing while parsing the failure.
  const envelope = (await response.json().catch(() => ({}))) as ErrorEnvelope
  return new ApiError(
    response.status,
    envelope.error?.code ?? 'UNKNOWN_ERROR',
    envelope.error?.message ?? response.statusText,
    envelope.error?.details ?? [],
    envelope.error?.params ?? {},
  )
}

let inFlightRefresh: Promise<boolean> | null = null

async function performRefresh(): Promise<boolean> {
  const csrf = readCsrfCookie()
  if (!csrf) {
    // No CSRF cookie means no refresh cookie either: the user is simply signed out.
    return false
  }
  const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { [CSRF_HEADER]: csrf, Accept: 'application/json' },
  })
  if (!response.ok) {
    return false
  }
  const body = (await response.json()) as { access_token: string }
  setAccessToken(body.access_token)
  return true
}

/**
 * Refresh the session at most once at a time, in this tab and across tabs.
 *
 * @returns whether a usable access token is now held.
 */
export async function refreshSession(): Promise<boolean> {
  if (inFlightRefresh) {
    return inFlightRefresh
  }

  const run = async (): Promise<boolean> => {
    // `navigator.locks` is absent in jsdom and in older browsers; the in-tab promise below still
    // prevents a refresh storm there, it just cannot coordinate between tabs.
    if (typeof navigator !== 'undefined' && navigator.locks) {
      return navigator.locks.request(REFRESH_LOCK, performRefresh)
    }
    return performRefresh()
  }

  inFlightRefresh = run().finally(() => {
    inFlightRefresh = null
  })
  return inFlightRefresh
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  /** Send cookies and the CSRF header — only the `/auth` routes need them. */
  withCredentials?: boolean
  /** Extra request headers. `Idempotency-Key` on money endpoints is the reason this exists. */
  headers?: Record<string, string>
  /** Internal: prevents an endless refresh → retry → refresh loop. */
  retryOnUnauthorised?: boolean
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const {
    method = 'GET',
    body,
    signal,
    withCredentials = false,
    retryOnUnauthorised = true,
    headers: extraHeaders,
  } = options

  const headers: Record<string, string> = { Accept: 'application/json', ...extraHeaders }
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`
  }
  if (withCredentials) {
    const csrf = readCsrfCookie()
    if (csrf) {
      headers[CSRF_HEADER] = csrf
    }
  }

  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      signal,
      credentials: withCredentials ? 'include' : 'same-origin',
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch (cause) {
    // fetch only rejects on network-level failure; HTTP errors are handled below.
    throw new ApiError(0, 'NETWORK', cause instanceof Error ? cause.message : 'Network error')
  }

  if (response.status === 401) {
    if (retryOnUnauthorised) {
      const refreshed = await refreshSession()
      if (refreshed) {
        return request<T>(path, { ...options, retryOnUnauthorised: false })
      }
    }
    // Both ways of arriving here mean the same thing: no token this client can obtain is accepted.
    //
    // The second way used to be silent. A 401 on the *retry* skipped this block entirely — the
    // session was over and nothing said so, so the app stayed on the page with its queries failing
    // and retrying behind it. What the operator saw was a screen that had stopped working without
    // telling them why, and the bell polling in the background made it happen while nobody was
    // looking at the tab.
    onSessionExpired?.()
  }

  if (!response.ok) {
    const error = await toApiError(response)
    // Every screen's request fails the same way until the password is changed, so the answer is
    // given once, here, rather than as a refusal on whichever screen happened to ask.
    if (error.status === 403 && error.code === PASSWORD_CHANGE_REQUIRED) {
      onPasswordChangeRequired?.()
    }
    throw error
  }
  if (response.status === 204) {
    return undefined as T
  }
  return (await response.json()) as T
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
  /**
   * Fetch a binary response — the Excel statement of SPEC.md §8.4.1 tab 4.
   *
   * Kept beside `request` rather than inside it because everything else in this client speaks
   * JSON, and a function that sometimes returns a Blob would push that check onto every call site.
   *
   * @example
   * const blob = await api.download(`/stores/${id}/statement.xlsx`)
   */
  download: async (path: string): Promise<Blob> => {
    const headers: Record<string, string> = {}
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`
    }
    const response = await fetch(`${API_BASE_URL}${path}`, { headers })
    if (!response.ok) {
      throw await toApiError(response)
    }
    return await response.blob()
  },
}
